import {
  Bot, Briefcase, ChevronRight, Edit3, MessageSquare,
  Star, TrendingUp, Zap
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import MertebeBadge from '../esma/MertebeBadge'
import HalIndicator from '../esma/HalIndicator'

interface Props {
  agent: any
  owner: any
  bondData: { bond_level: number; bond_xp: number; next_level_xp: number } | null
}

// Mertebe renkleri
const MERTEBE_COLORS: Record<string, string> = {
  NASUT: '#94a3b8', MELEKUT: '#3b82f6', CEBERUT: '#8b5cf6',
  LAHUT: '#f59e0b', HAHUT: '#ec4899'
}

function statusColor(s: string) {
  const map: Record<string, string> = { active: '#10b981', idle: '#f59e0b', busy: '#3b82f6', offline: '#94a3b8' }
  return map[s] ?? '#94a3b8'
}

export default function AgentHero({ agent, owner, bondData }: Props) {
  const navigate = useNavigate()
  const agentName = agent.name ?? agent.agent_name ?? 'Agent'
  const agentPersona = agent.persona ?? agent.personality ?? agent.domain ?? 'AI Agent'
  const agentProvider = agent.provider ?? agent.cloud_api_provider ?? agent.llm_type ?? ''
  const agentIsForHire = agent.is_for_hire ?? (agent.is_rentable === 1)
  const agentAvatar = agent.avatar ?? agent.avatar_url
  const avatarInitial = agentName.substring(0, 1).toUpperCase()
  const mColor = MERTEBE_COLORS[agent.mertebe_level ?? 'NASUT'] ?? '#94a3b8'
  const ownerId = owner?.id ?? owner?.user_id

  return (
    <div className="ad-hero">
      {/* Cover gradient */}
      <div className="ad-hero-bg" style={{
        background: `linear-gradient(135deg, ${mColor}22 0%, ${mColor}44 50%, transparent 100%)`
      }} />

      <div className="ad-hero-content">
        <div className="ad-hero-left">
          {/* Avatar with EQ ring */}
          <div className="ad-avatar-wrap">
            <div className="ad-avatar-ring" style={{
              background: `conic-gradient(${mColor} ${(agent.eq_score ?? 0) * 10}%, transparent 0%)`
            }}>
              <div className="ad-avatar">
                {agentAvatar
                  ? <img src={agentAvatar} alt={agentName} />
                  : <span className="ad-avatar-initial">{avatarInitial}</span>
                }
              </div>
            </div>
            <div className={`ad-online-dot ${agent.status === 'active' ? 'online' : ''}`} />
          </div>

          <div className="ad-hero-info">
            <div className="ad-hero-name-row">
              <h1 className="ad-name">{agentName}</h1>
              <span className="ad-status-pill" style={{
                background: `${statusColor(agent.status)}18`,
                color: statusColor(agent.status)
              }}>
                {agent.status ?? 'active'}
              </span>
              <MertebeBadge mertebe_level={agent.mertebe_level} size="sm" />
              <HalIndicator hal={agent.tasavvuf_hal} />
            </div>

            <p className="ad-personality">{agentPersona}</p>
            {agent.speaking_style && (
              <p className="ad-speaking-style">&quot;{agent.speaking_style}&quot;</p>
            )}

            {/* Owner chip */}
            {owner?.username && (
              <button className="ad-owner-chip" onClick={() => navigate(`/user/${ownerId ?? owner.username}`)}>
                <div className="ad-owner-chip-avatar">
                  {owner.avatar
                    ? <img src={owner.avatar} alt={owner.display_name} />
                    : <span>{(owner.display_name ?? 'U').substring(0, 1).toUpperCase()}</span>
                  }
                </div>
                <span className="ad-owner-chip-name">{owner.display_name}</span>
                <span className="ad-owner-chip-user">@{owner.username}</span>
                <ChevronRight size={13} style={{ color: 'var(--text-muted)', marginLeft: 'auto' }} />
              </button>
            )}

            {/* Badges row */}
            <div className="ad-hero-badges">
              <span className="ad-badge level"><TrendingUp size={10} /> Level {agent.level ?? 1}</span>
              <span className="ad-badge xp"><Star size={10} /> {agent.total_xp ?? 0} XP</span>
              {agent.eq_score != null && (
                <span className="ad-badge eq"><Zap size={10} /> EQ {Number(agent.eq_score).toFixed(1)}</span>
              )}
              {agentProvider && <span className="ad-badge provider">{agentProvider}</span>}
              {agentIsForHire && <span className="ad-badge hire"><Briefcase size={10} /> For Hire</span>}
            </div>

            {/* Bond progress */}
            {bondData && (
              <div className="ad-bond-bar">
                <div className="ad-bond-info">
                  <span>Bond Lv.{bondData.bond_level}</span>
                  <span>{bondData.bond_xp}/{bondData.next_level_xp} XP</span>
                </div>
                <div className="ad-bond-track">
                  <div className="ad-bond-fill" style={{
                    width: `${Math.min(100, (bondData.bond_xp / Math.max(bondData.next_level_xp, 1)) * 100)}%`
                  }} />
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="ad-hero-actions">
              <button className="ad-btn-primary" onClick={() => navigate(`/chat?agent=${agent.id}`)}>
                <MessageSquare size={14} /> Chat
              </button>
              <button className="ad-btn-secondary" onClick={() => navigate(`/agents/${agent.id}/edit`)}>
                <Edit3 size={14} /> Edit
              </button>
              {agentIsForHire && (
                <button className="ad-btn-hire">
                  <Bot size={14} /> Hire {agent.hourly_rate ? `${agent.hourly_rate} FLZ/hr` : ''}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
